import useCloudStore from '../store/cloudStore';
import { useT } from '../i18n';

/**
 * "Cloud save found" gate. Shown whenever a signed-in player has a match stored under
 * `users/{uid}/games/activeGame` — either resume it or start a fresh match.
 */
export const CloudSavePrompt = () => {
  const t = useT();
  const { promptOpen, pending, user, acceptResume, declineResume, dismissPrompt } = useCloudStore();
  if (!promptOpen || !pending || !user) return null;

  const alive = pending.players.filter((p) => !p.isBankrupt).length;
  const humans = pending.players.filter((p) => !p.isAI).length;
  const savedAt = new Date(pending.savedAt).toLocaleString();

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-sky-400/60 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-sky-600/90 to-slate-900 px-4 py-3">
          <h2 className="text-sm font-extrabold text-white">{t('cloud.resumeTitle')}</h2>
          <button type="button" onClick={dismissPrompt} className="rounded-md bg-slate-800/80 px-2 py-1 text-[10px] font-bold text-slate-300 hover:bg-slate-700">
            {t('common.close')}
          </button>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-sm text-slate-200">
            {t('cloud.resumeBody', {
              name: user.displayName,
              round: pending.market.currentRound,
              phase: pending.market.phase,
              players: `${alive} (${humans} human)`,
            })}
          </p>
          <div className="rounded-xl border border-slate-700/60 bg-slate-950/60 p-2.5 text-[11px] text-slate-400">
            <p>
              {t('cloud.saved', { time: savedAt, round: pending.market.currentRound })} · ${' '}
              {pending.players.map((p) => `${p.name} ${Math.round(p.cash).toLocaleString()}`).join(' · ')}
            </p>
            <p className="mt-1 text-slate-500">{t('cloud.historyNote', { email: user.email || user.uid, uid: user.uid })}</p>
          </div>
          <div className="grid gap-2">
            <button
              type="button"
              onClick={acceptResume}
              className="min-h-[46px] rounded-xl border-2 border-emerald-400/70 bg-gradient-to-r from-emerald-600/90 to-teal-600/90 px-4 py-2 text-sm font-extrabold text-white transition hover:from-emerald-500 hover:to-teal-500"
            >
              {t('cloud.resumeCta')}
            </button>
            <button
              type="button"
              onClick={declineResume}
              className="min-h-[46px] rounded-xl border-2 border-slate-600 bg-slate-800 px-4 py-2 text-sm font-extrabold text-slate-200 transition hover:bg-slate-700"
            >
              {t('cloud.newCta')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudSavePrompt;
